import React, { useEffect } from 'react';
import {
    ActionIcon,
    Button,
    Flex,   
    Loader,
    Paper,
    ScrollArea,
    Stack,
    Table,
    Text,
    Title,
} from '@mantine/core';
import { inject, observer } from 'mobx-react';
import { theme } from '../../../global/style/theme.tsx';
import { useTranslation } from 'react-i18next';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import { useJournals, useDeleteJournal } from '../../../global/entities/Journal/queries/useJournals.ts';
import { IJournal } from '../../../global/entities/Journal/Journal.ts';
import { IconArrowRight, IconPlus, IconTrash } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import { modals } from '@mantine/modals';

const JournalTable: React.FC = inject()(
    observer(() => {
        const { t } = useTranslation();
        const { data: journals, isLoading, refetch } = useJournals();
        const { mutateAsync: deleteJournal } = useDeleteJournal();

        const { setSorting, sortBy, reverseSortDirection, setSortedData, sortedData } = useTable();
        const navigate = useNavigate();

        useEffect(() => {
            setSortedData([]);

            if (journals?.length) 
                setSortedData(journals);
            
        }, [journals]);

        const openModalDelete = (journalId: string) =>
            modals.openConfirmModal({
                title: t('modal.deleteItemModal.title'),
                children: (
                    <Stack gap={'sm'}>
                        <Text>{t('modal.deleteItemModal.question')}</Text>
                        <Text fw={'bold'}> {t('modal.deleteItemModal.details')}</Text>
                    </Stack>
                ),
                labels: { confirm: t('button.delete'), cancel: t('button.cancel') },
                confirmProps: { color: 'red' },
                cancelProps: { variant: 'outline', color: 'gray' },
                onCancel: () => console.log('Suppression annulée'),
                onConfirm: () => {
                    onClickDelete(journalId);
                },
                size: 'lg',
                radius: 'xs',
            });

        const onClickDelete = (journalId: string) => {
            deleteJournal(Number(journalId))
                .then(() => {
                    refetch();
                })
                .catch((error) => {
                    console.error('Erreur lors de la suppression :', error);
                });
        };

        const handleEdit = (journal: IJournal) => {
            navigate(`/adminDashboard/journal/${journal.id}`);
        };

        if (isLoading) 
            return <Loading visible={true} loadingMessage={t('common.loading')} />;

        return (
            <>
                <Flex mb={'lg'} mt={'lg'} align={'center'} justify={'space-between'}>
                    <Button onClick={() => navigate('/adminDashboard/hcp')} variant="transparent">
                        <Title c="black" order={4}>
                            {t('journal.title')}
                        </Title>
                    </Button>
                    <Button
                        onClick={() => navigate('/adminDashboard/journal/0')}
                        leftSection={<IconPlus />}
                        styles={{ label: { align: 'center', justifyContent: 'center' } }}
                    >
                        {t('journal.addNewJournal')}
                    </Button>
                </Flex>
                <Paper w="100%">
                    <Stack gap={'md'} h={'min-content'}>
                        <ScrollArea w="100%">
                            <Table verticalSpacing="xs">
                                <Table.Thead>
                                    <Table.Tr>
                                        <Th
                                            sorted={sortBy === 'name'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('name', sortedData)}
                                        >
                                            {t('formGlobal.name')}
                                        </Th>

                                        <Th
                                            sorted={sortBy === 'therapeuticIndicationName'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('therapeuticIndicationName', sortedData)}
                                        >
                                            {t('journal.therapeuticIndication')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'sexNames'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('sexNames', sortedData)}
                                        >
                                            {t('formGlobal.sex')}
                                        </Th>
                                        <Table.Th></Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {sortedData?.map((journal: IJournal, index: number) => {
                                        return (
                                            <Table.Tr
                                                key={journal.id}
                                                id={journal.id?.toString()}
                                                style={{
                                                    background:
                                                        index % 2 === 1
                                                            ? undefined
                                                            : 'var(--Background-Gray-light, #E9ECEF)',
                                                    pointer: 'cursor',
                                                }}
                                            >
                                                <Table.Td>
                                                    <Text
                                                        styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                        truncate
                                                        maw={200}
                                                    >
                                                        {journal.name}
                                                    </Text>
                                                </Table.Td>

                                                <Table.Td>
                                                    <Text
                                                        styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                        truncate
                                                    >
                                                        {journal.therapeuticIndicationName}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text
                                                        styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                        truncate
                                                    >
                                                        {(journal.sexNames || []).join(', ')}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Flex align={'center'}>
                                                        <ActionIcon
                                                            tabIndex={-1}
                                                            variant="subtle"
                                                            color="red"
                                                            onClick={() => openModalDelete(journal.id!.toString())}
                                                        >
                                                            <IconTrash size={18} />
                                                        </ActionIcon>
                                                        <ActionIcon variant="transparent">
                                                            {isLoading ? (
                                                                <Loader
                                                                    size="xs"
                                                                    color={theme.colors?.blue?.toString()}
                                                                />
                                                            ) : (
                                                                <IconArrowRight
                                                                    onClick={() => handleEdit(journal)}
                                                                    color={theme.colors?.blue?.toString()}
                                                                />
                                                            )}
                                                        </ActionIcon>
                                                    </Flex>
                                                </Table.Td>
                                            </Table.Tr>
                                        );
                                    })}
                                </Table.Tbody>
                            </Table>
                        </ScrollArea>
                    </Stack>
                </Paper>
            </>
        );
    }),
);

const WrappedJournalTable: React.FC = () => {
    return (
        <TableContextProvider>
            <JournalTable />
        </TableContextProvider>
    );
};

export default WrappedJournalTable; 